import leaflet from 'leaflet';

/**
 * Returns a cache-stable identity for an avatar/photo URL.
 *
 * S3 presigned URLs are re-signed periodically by the backend (`X-Amz-Date`
 * and `X-Amz-Signature` change), so the full URL string churns even when the
 * underlying object is the same. For caching purposes we want
 * "same S3 object → same key", so we strip the query string for any URL
 * carrying an `X-Amz-Signature`. Non-S3 URLs (gravatar, etc.) are stable
 * already and pass through untouched.
 *
 * The cached HTML SafeString itself still embeds the original (signed) URL in
 * its `<img src>`; only the cache *key* is normalized. That URL is valid for
 * 24h (`X-Amz-Expires=86400`) and the backend evicts profile data records
 * ≥10 min before expiry, so any URL we receive has substantial validity left
 * when first cached.
 *
 * @param {string|null|undefined} url
 * @returns {string}
 */
export function stableImageId(url) {
  if (!url) return '';
  if (!url.includes('X-Amz-Signature')) return url;
  const q = url.indexOf('?');
  return q === -1 ? url : url.slice(0, q);
}

/**
 * Memoizes an icon's inner-markup SafeString by key. Returns the **same
 * SafeString reference** for identical keys so downstream layers (the DivIcon
 * cache) can also short-circuit.
 *
 * @template T
 * @param {Map<string, T>} cache
 * @param {string} key
 * @param {() => T} factory
 * @returns {T}
 */
export function getCachedIconHtml(cache, key, factory) {
  // eslint-disable-next-line ember/use-ember-get-and-set
  let cached = cache.get(key);
  if (cached === undefined) {
    cached = factory();
    // eslint-disable-next-line ember/use-ember-get-and-set
    cache.set(key, cached);
  }
  return cached;
}

/**
 * Memoizes a Leaflet `DivIcon` by the composite of its determining state.
 *
 * Returns the **same DivIcon reference** for identical state so ember-leaflet's
 * `{{did-update @icon}}` sees an unchanged `===` value, and the `setIcon` guard
 * short-circuits the DOM rebuild (and the consequent `<img>` refetch).
 *
 * For the live-map zoom optimization, `iconSize` and `iconAnchor` are passed
 * by the component as **constants per marker** (computed against `baseSize`
 * + selection state, not against the current zoom-derived size), so they're
 * deliberately *not* part of the cache key — zoom no longer invalidates a
 * marker's icon. The visual scaling is applied by a CSS `transform` on a
 * `[data-marker-scale]` wrapper inside `html`, driven by a zoom listener
 * outside this function. The 14 px content-hide threshold is encoded into
 * `className` (via `is-glyph-hidden`), so crossing it still produces a
 * cache MISS and a legitimate rebuild via the option-1 path.
 *
 * Within a marker's lifetime, `className` carries its state
 * (`is-selected`/`is-scheduled`/`is-glyph-hidden`/availability/type/per-feature
 * id) and `html` carries the inner markup; together they fully determine
 * which DivIcon to return.
 *
 * @param {Map<string, leaflet.DivIcon>} cache
 * @param {{ className: string, iconSize: number[], iconAnchor: number[], html: unknown }} opts
 * @returns {leaflet.DivIcon}
 */
export function getCachedDivIcon(cache, { className, iconSize, iconAnchor, html }) {
  const htmlStr = html == null ? '' : html.toString();
  // Non-empty html → marker is drawn via a `[data-marker-scale]` wrapper sized
  // at `baseSize`, so `iconSize`/`iconAnchor` are constants per marker and
  // don't need to be in the key. Zoom is handled by the CSS transform.
  //
  // Empty html → marker is rendered purely from `className` CSS (e.g.
  // `.is-selected`'s red-pin background applied directly to the icon
  // element). There's no child wrapper to scale, so size *is* zoom-derived
  // and must be in the key — these fall back to the option-1 rebuild path.
  const key =
    htmlStr === '' ? `${className}|${iconSize.join(',')}|${iconAnchor.join(',')}|` : `${className}|${htmlStr}`;
  // eslint-disable-next-line ember/use-ember-get-and-set
  let icon = cache.get(key);
  if (icon === undefined) {
    icon = new leaflet.DivIcon({ iconSize, iconAnchor, className, html: htmlStr });
    // eslint-disable-next-line ember/use-ember-get-and-set
    cache.set(key, icon);
  }
  return icon;
}
